import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import Modal from 'components/commons/modal/Modal2';
import { CURRENCY, Currency } from 'config/consts';
import { currencyToFlag } from '../utils/currency';
import * as useAPI from 'hooks/useAPI';
import { getCurrencyPairs } from 'config/api';

type Rate = {
  currency: Currency;
  amount: number;
  name: string;
};

type ExchangeRateItemProps = {
  rate: Rate;
};

const ExchangeRateItem = ({ rate }: ExchangeRateItemProps) => {
  // memo: APIから取得するレートの小数桁が5桁であること前提
  const getAmount = () => {
    if (rate.amount < 1) {
      return rate.amount.toFixed(5);
    } else if (rate.amount < 10) {
      const num = Math.floor(rate.amount * 10000) / 10000;
      return num.toFixed(4);
    } else {
      const num = Math.floor(rate.amount * 1000) / 1000;
      return num.toFixed(3);
    }
  };

  return (
    <>
      <div className="flex py-2 items-center">
        <img className="h-4" alt="外国旗" src={currencyToFlag(rate.currency)} />
        <img
          className="h-4"
          alt="日本国旗"
          src="/static/images/national-flag/JP.svg"
        />
        <div className="ml-2 text-sm">{rate.name.replace(/\/?円/, '/円')}</div>
        <div className="ml-auto font-bold text-gray-800">{getAmount()}</div>
      </div>
    </>
  );
};

type ExchangeRateListProps = {
  rates: Rate[];
};

const ExchangeRateList = ({ rates }: ExchangeRateListProps) => {
  const Line = () => <div className="bg-gray-400" style={{ height: '1px' }} />;
  return (
    <>
      <div className="pt-4">
        {rates.map((rate, index) => (
          <React.Fragment key={index}>
            <Line />
            <ExchangeRateItem {...{ rate }} />
          </React.Fragment>
        ))}
        <Line />
      </div>
    </>
  );
};

const currencySortList = [
  { num: 1, currency: 'USD' },
  { num: 2, currency: 'EUR' },
  { num: 3, currency: 'GBP' },
  { num: 4, currency: 'AUD' },
  { num: 5, currency: 'TRY' },
  { num: 6, currency: 'ZAR' },
  { num: 7, currency: 'NZD' },
  { num: 8, currency: 'BRL' },
  { num: 9, currency: 'CAD' },
  { num: 10, currency: 'CHF' },
  { num: 11, currency: 'SGD' },
  { num: 12, currency: 'HKD' },
  { num: 13, currency: 'CNY' },
  { num: 14, currency: 'KRW' },
  { num: 15, currency: 'THB' },
  { num: 16, currency: 'MYR' },
  { num: 17, currency: 'IDR' },
  { num: 18, currency: 'PHP' },
  { num: 19, currency: 'VND' },
  { num: 20, currency: 'TWD' },
  { num: 21, currency: 'MXN' },
  { num: 22, currency: 'RUB' },
  { num: 23, currency: 'INR' },
  { num: 24, currency: 'SEK' },
  { num: 25, currency: 'NOK' },
  { num: 26, currency: 'DKK' },
  { num: 27, currency: 'PLN' },
];

type AboutExchangeRateModalProps = {
  isShowModal: boolean;
  hideModal: () => void;
};

export const AboutExchangeRateModal = ({
  isShowModal,
  hideModal,
}: AboutExchangeRateModalProps) => {
  const { state } = useAboutExchangeRateModalContext();
  const rates = useAPI.isLoaded(state) ? state.rate.rates : [];
  if (useAPI.isLoading(state)) return <></>;
  const exchangeRateDate = state.rate.date;

  const sortedRates = rates.sort((first, second) => {
    const firstNum = currencySortList.find(
      list => list.currency === first.currency
    )?.num;
    const secontNum = currencySortList.find(
      list => list.currency === second.currency
    )?.num;

    if (firstNum === undefined || secontNum === undefined) {
      return 0;
    } else if (firstNum > secontNum) {
      return 1;
    } else if (firstNum < secontNum) {
      return -1;
    } else {
      return 0;
    }
  });

  return (
    <Modal
      close={hideModal}
      isShow={isShowModal}
      closeOnClickOutsideWindow
      modalBaseClass="max-w-80">
      <div className="max-h-modal overflow-y-auto">
        <div className="flex mt-2 mx-3 p-2">
          <div className="flex-grow text-lg font-bold text-gray-800">
            換算レートについて
          </div>
          <button>
            <i
              className="icon-close text-2xl text-gray-500 text-center cursor-pointer"
              onClick={hideModal}></i>
          </button>
        </div>
        <div className="ui-border-b">
          <div className="mb-2 mx-3 p-2">
            ({exchangeRateDate.getFullYear()}/
            {(exchangeRateDate.getMonth() + 1).toString()}/
            {exchangeRateDate.getDate()})
            <ExchangeRateList {...{ rates: sortedRates }} />
            <div className="pt-4 text-bluegray-500 text-sm">
              ※外貨資産は円換算して表示しています。
              <br />
              ※毎日9:00時点（日本時間）に集計された上記為替レートを外貨資産額に掛けて円換算しています。
              <br />
              ※為替レートは1日1回更新されます。
              <br />
              ※外国株式の評価損益（円）は、購入日の為替レートと上記為替レートを用いて評価額を算出し、その差分を表示しています。
              <br />
              ※円換算時の端数は1円未満を切り捨てとしています。
            </div>
          </div>
        </div>
        <div
          onClick={hideModal}
          className="flex justify-center text-gray-500 m-1 p-2 cursor-pointer">
          閉じる
        </div>
      </div>
    </Modal>
  );
};

type State = {
  rates: Rate[];
  date: Date;
};

const AboutExchangeRateModalContext = createContext<{
  state: useAPI.State<'rate', State>;
}>({
  state: useAPI.loading,
});

const AboutExchangeRateModalSetterContext = createContext<{
  setState: Dispatch<SetStateAction<useAPI.State<'rate', State>>>;
}>({
  setState: () => {
    return;
  },
});

export const AboutExchangeRateModalProvider = ({
  children,
}: {
  children: React.ReactNode | null;
}) => {
  const [state, setState] = useState<useAPI.State<'rate', State>>(
    useAPI.loading
  );
  const api = async () => {
    const rates = await getCurrencyPairs();
    // return {
    //   rates: Array.from({ length: Math.floor(Math.random() * 9) + 5 }).map(
    //     _ => ({
    //       currency: Object.values(CURRENCY)[Math.floor(Math.random() * 17)],
    //       amount: Math.random() * 50 + 120,
    //     })
    //   ),
    // };
    const item = rates?.Items ? rates.Items[0] : null;
    return {
      rates: (rates.Items ?? []).flatMap(item => {
        const id = item.ID.replace('JPY', '').replace(/\//, '');
        if (!Object.values(CURRENCY).includes(id as Currency)) return [];
        return [
          {
            currency: id as Currency,
            amount: item.Rate,
            name: item.Name,
          },
        ];
      }),
      date: item?.RateDateTime
        ? new Date(item.RateDateTime.replace(/\//g, '-'))
        : new Date(),
    };
  };
  const load = async () => {
    const state = await api();
    setState(useAPI.loaded({ rate: state }));
  };
  useEffect(() => {
    if (useAPI.isLoading(state)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <AboutExchangeRateModalContext.Provider value={{ state }}>
      <AboutExchangeRateModalSetterContext.Provider value={{ setState }}>
        {children}
      </AboutExchangeRateModalSetterContext.Provider>
    </AboutExchangeRateModalContext.Provider>
  );
};

export const useAboutExchangeRateModalContext = (): {
  state: useAPI.State<'rate', State>;
} => {
  const context = useContext(AboutExchangeRateModalContext);
  return { state: context.state };
};
